
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Clock, BookOpen, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageLayout } from '@/components/layout/PageLayout';
import { useToast } from '@/hooks/use-toast';
import { getLearningModuleById } from '@/services/learning-service';
import { useLearningProgress } from '@/hooks/use-learning-progress';
import { LearningModule } from '@/types/learning';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';

const ModuleContent = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [module, setModule] = useState<LearningModule | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const { markModuleCompleted, isUpdating } = useLearningProgress();

  useEffect(() => {
    const fetchModule = async () => {
      if (!moduleId) return;
      
      try {
        const moduleData = await getLearningModuleById(moduleId, user?.id);
        setModule(moduleData);
        setIsCompleted(moduleData.isCompleted || false);
      } catch (error) {
        console.error('Error fetching module content:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load module content",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchModule();
  }, [moduleId, user?.id, toast]);

  const handleComplete = async () => {
    if (!moduleId || !user) return;
    
    const success = await markModuleCompleted(moduleId, !isCompleted);
    if (success) {
      setIsCompleted(!isCompleted);
    }
  };

  const handleBackToPath = () => {
    // If we have a pathId in the module, navigate back to that path
    if (module?.pathId) {
      navigate(`/learning-paths/${module.pathId}`);
    } else {
      // Otherwise just go back to all learning paths
      navigate('/learning-paths');
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-6 w-40 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!module) {
    return (
      <PageLayout>
        <div className="container mx-auto px-4 py-6 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Module Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The module you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={handleBackToPath}>Return to Learning Paths</Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6">
        <Button variant="ghost" onClick={handleBackToPath} className="mb-6 pl-0">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Learning Path
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-start mb-2">
                <CardTitle className="text-2xl font-bold font-lexend">{module.title}</CardTitle>
                <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                  <Clock className="h-4 w-4 mr-1" />
                  {module.duration}
                </div>
              </div>
              <div className="flex items-center text-sm text-muted-foreground">
                <BookOpen className="h-4 w-4 mr-1" />
                <span className="mr-3">{module.level}</span>
                {isCompleted && (
                  <span className="flex items-center text-green-600">
                    <Check className="h-4 w-4 mr-1" />
                    Completed
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm md:prose-base max-w-none">
                {module.content ? (
                  <div 
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ 
                      __html: module.content
                        // Allow HTML in markdown for videos and iframes
                        .replace(/<iframe.*?<\/iframe>/g, (match) => match)
                        .replace(/<video.*?<\/video>/g, (match) => match)
                    }}
                  />
                ) : (
                  <p>No content available for this module.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end mt-6">
            <Button 
              onClick={handleComplete} 
              className="min-w-32"
              disabled={isUpdating}
            >
              {isCompleted ? 'Mark as Incomplete' : 'Mark as Complete'}
            </Button>
          </div>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default ModuleContent;
